import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  buildInterlockPieces,
  tabPadFraction,
} from "./pieceGeometry";
import "./freeformJigsaw.css";

function derangeIndices(n) {
  const order = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  for (let i = 0; i < n; i += 1) {
    if (order[i] === i) {
      const swapWith = i === 0 ? 1 : i - 1;
      [order[i], order[swapWith]] = [order[swapWith], order[i]];
    }
  }
  return order;
}

/** Translate an SVG path by (dx, dy). Handles M/L/C absolute commands from our generator. */
function translatePath(d, dx, dy) {
  return d.replace(
    /([MLC])\s*([-\d.eE]+)[\s,]+([-\d.eE]+)(?:[\s,]+([-\d.eE]+)[\s,]+([-\d.eE]+)[\s,]+([-\d.eE]+)[\s,]+([-\d.eE]+))?/g,
    (match, cmd, ...rest) => {
      if (cmd === "Z" || cmd === "z") return match;
      if (cmd === "C") {
        const [x1, y1, x2, y2, x, y] = rest.map(Number);
        return `C ${(x1 + dx).toFixed(3)} ${(y1 + dy).toFixed(3)} ${(x2 + dx).toFixed(3)} ${(y2 + dy).toFixed(3)} ${(x + dx).toFixed(3)} ${(y + dy).toFixed(3)}`;
      }
      const x = Number(rest[0]);
      const y = Number(rest[1]);
      return `${cmd} ${(x + dx).toFixed(3)} ${(y + dy).toFixed(3)}`;
    }
  );
}

/** Map board coords → screen using the rendered SVG box (viewBox 0 0 boardW boardH). */
function boardToScreen(svg, boardX, boardY, boardW, boardH) {
  const rect = svg.getBoundingClientRect();
  const scaleX = rect.width / boardW;
  const scaleY = rect.height / boardH;
  return {
    x: rect.left + boardX * scaleX,
    y: rect.top + boardY * scaleY,
    scaleX,
    scaleY,
    rect,
  };
}

/** Map screen → board coords. */
function screenToBoard(svg, clientX, clientY, boardW, boardH) {
  const rect = svg.getBoundingClientRect();
  return {
    x: ((clientX - rect.left) / rect.width) * boardW,
    y: ((clientY - rect.top) / rect.height) * boardH,
    rect,
  };
}

/**
 * Interlocking (or square) SVG jigsaw: drag to swap; correct pieces lock.
 */
export default function FreeformJigsawSvg({
  imageUrl,
  rows,
  cols,
  boardW,
  boardH,
  puzzleType = "interlock",
  variant = "classic",
  onComplete,
  onPuzzleSolved,
  onCorrectPieceCountChange,
  disabled = false,
}) {
  const useSquare = puzzleType === "square" || variant === "square";
  const uid = useId().replace(/:/g, "");

  const geometry = useMemo(() => {
    if (useSquare) {
      const cellW = boardW / cols;
      const cellH = boardH / rows;
      return Array.from({ length: rows * cols }, (_, index) => {
        const row = Math.floor(index / cols);
        const col = index % cols;
        const x0 = col * cellW;
        const y0 = row * cellH;
        return {
          id: `piece-${index}`,
          originalIndex: index,
          row,
          col,
          path: `M ${x0} ${y0} L ${x0 + cellW} ${y0} L ${x0 + cellW} ${y0 + cellH} L ${x0} ${y0 + cellH} Z`,
        };
      });
    }
    return buildInterlockPieces(rows, cols, boardW, boardH, {
      tabSize: variant === "irregular" ? 0.2 : 0.16,
    });
  }, [rows, cols, boardW, boardH, useSquare, variant]);

  const [placement, setPlacement] = useState(() =>
    derangeIndices(rows * cols)
  );
  const [locked, setLocked] = useState(() =>
    Array.from({ length: rows * cols }, () => false)
  );
  const [solved, setSolved] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [ghost, setGhost] = useState(null);

  const placementRef = useRef(placement);
  const lockedRef = useRef(locked);
  const dragRef = useRef(null);
  const overRef = useRef(null);
  const completedRef = useRef(false);
  const rafRef = useRef(0);
  const svgRef = useRef(null);
  const ghostElRef = useRef(null);
  const guideElsRef = useRef([]);
  const onCompleteRef = useRef(onComplete);
  const onPuzzleSolvedRef = useRef(onPuzzleSolved);
  const onCorrectRef = useRef(onCorrectPieceCountChange);

  const cw = boardW / cols;
  const ch = boardH / rows;
  const pad = useSquare ? 0 : tabPadFraction(0.16) * Math.min(cw, ch);

  useEffect(() => {
    placementRef.current = placement;
  }, [placement]);

  useEffect(() => {
    lockedRef.current = locked;
  }, [locked]);

  useEffect(() => {
    onCompleteRef.current = onComplete;
    onPuzzleSolvedRef.current = onPuzzleSolved;
    onCorrectRef.current = onCorrectPieceCountChange;
  }, [onComplete, onPuzzleSolved, onCorrectPieceCountChange]);

  useEffect(() => {
    setPlacement(derangeIndices(rows * cols));
    setLocked(Array.from({ length: rows * cols }, () => false));
    setSolved(false);
    setShowFull(false);
    completedRef.current = false;
  }, [rows, cols, imageUrl, boardW, boardH, useSquare, variant]);

  const setOverHighlight = useCallback((nextOver) => {
    const prev = overRef.current;
    if (prev === nextOver) return;
    if (prev != null) {
      guideElsRef.current[prev]?.classList.remove(
        "freeform-jigsaw__guide--over"
      );
    }
    if (nextOver != null) {
      guideElsRef.current[nextOver]?.classList.add(
        "freeform-jigsaw__guide--over"
      );
    }
    overRef.current = nextOver;
  }, []);

  const reportCorrect = useCallback((nextPlacement, nextLocked) => {
    let n = 0;
    for (let i = 0; i < nextPlacement.length; i += 1) {
      if (nextPlacement[i] === i || nextLocked[i]) n += 1;
    }
    onCorrectRef.current?.(n);
  }, []);

  const finishIfComplete = useCallback((nextPlacement) => {
    const done = nextPlacement.every((pieceId, slot) => pieceId === slot);
    if (!done) return;
    setSolved(true);
    window.setTimeout(() => setShowFull(true), 350);
    if (!completedRef.current) {
      completedRef.current = true;
      onPuzzleSolvedRef.current?.();
      onCompleteRef.current?.();
    }
  }, []);

  const swapSlots = useCallback(
    (fromSlot, toSlot) => {
      if (fromSlot == null || toSlot == null || fromSlot === toSlot) return;
      if (lockedRef.current[fromSlot] || lockedRef.current[toSlot]) return;

      const next = placementRef.current.slice();
      [next[fromSlot], next[toSlot]] = [next[toSlot], next[fromSlot]];

      const nextLocked = lockedRef.current.slice();
      next.forEach((pieceId, slot) => {
        if (pieceId === slot) nextLocked[slot] = true;
      });

      setPlacement(next);
      setLocked(nextLocked);
      reportCorrect(next, nextLocked);
      finishIfComplete(next);
    },
    [finishIfComplete, reportCorrect]
  );

  const placeGhost = useCallback((clientX, clientY) => {
    const d = dragRef.current;
    const el = ghostElRef.current;
    if (!d || !el) return;
    el.style.transform = `translate3d(${clientX - d.offsetX}px, ${clientY - d.offsetY}px, 0)`;
  }, []);

  const clearDrag = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    setOverHighlight(null);
    dragRef.current = null;
    ghostElRef.current = null;
    setDragIndex(null);
    setGhost(null);
    document.body.classList.remove("masterpiece-dragging");
  }, [setOverHighlight]);

  const slotFromPoint = useCallback(
    (clientX, clientY) => {
      const svg = svgRef.current;
      if (!svg) return null;
      const { x, y, rect } = screenToBoard(
        svg,
        clientX,
        clientY,
        boardW,
        boardH
      );
      if (
        clientX < rect.left ||
        clientY < rect.top ||
        clientX > rect.right ||
        clientY > rect.bottom
      ) {
        return null;
      }
      const col = Math.min(cols - 1, Math.max(0, Math.floor(x / cw)));
      const row = Math.min(rows - 1, Math.max(0, Math.floor(y / ch)));
      return row * cols + col;
    },
    [boardW, boardH, cols, rows, cw, ch]
  );

  const bindGhostEl = useCallback((node) => {
    ghostElRef.current = node;
    const d = dragRef.current;
    if (node && d) {
      node.style.transform = `translate3d(${d.startX}px, ${d.startY}px, 0)`;
    }
  }, []);

  const onPointerDown = useCallback(
    (e, slotIndex) => {
      if (disabled || solved) return;
      if (lockedRef.current[slotIndex]) return;
      if (e.button != null && e.button !== 0) return;

      const svg = svgRef.current;
      const pieceId = placementRef.current[slotIndex];
      const geo = geometry[pieceId];
      if (!svg || !geo) return;

      e.preventDefault();
      e.stopPropagation();

      const clientX = e.clientX;
      const clientY = e.clientY;
      const pointerId = e.pointerId;

      try {
        e.currentTarget.setPointerCapture?.(pointerId);
      } catch {
        /* ignore */
      }

      const slotCol = slotIndex % cols;
      const slotRow = Math.floor(slotIndex / cols);
      const homeX = geo.col * cw;
      const homeY = geo.row * ch;
      const slotX = slotCol * cw;
      const slotY = slotRow * ch;
      const dx = slotX - homeX;
      const dy = slotY - homeY;

      const ghostBoardX = slotX - pad;
      const ghostBoardY = slotY - pad;
      const { x: screenX, y: screenY, scaleX, scaleY } = boardToScreen(
        svg,
        ghostBoardX,
        ghostBoardY,
        boardW,
        boardH
      );
      const ghostW = (cw + pad * 2) * scaleX;
      const ghostH = (ch + pad * 2) * scaleY;

      dragRef.current = {
        slotIndex,
        pointerId,
        offsetX: clientX - screenX,
        offsetY: clientY - screenY,
        startX: screenX,
        startY: screenY,
      };
      setOverHighlight(null);
      setDragIndex(slotIndex);
      setGhost({
        width: ghostW,
        height: ghostH,
        path: translatePath(geo.path, dx - ghostBoardX, dy - ghostBoardY),
        imgX: dx - ghostBoardX,
        imgY: dy - ghostBoardY,
        viewW: cw + pad * 2,
        viewH: ch + pad * 2,
      });
      document.body.classList.add("masterpiece-dragging");

      requestAnimationFrame(() => {
        placeGhost(clientX, clientY);
      });
    },
    [
      disabled,
      solved,
      geometry,
      cols,
      cw,
      ch,
      boardW,
      boardH,
      pad,
      placeGhost,
      setOverHighlight,
    ]
  );

  // Stable window listeners — do NOT clearDrag on dep rebind (timer re-renders
  // were cancelling the active drag mid-move).
  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      if (d.pointerId != null && e.pointerId !== d.pointerId) return;
      const { clientX, clientY } = e;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        placeGhost(clientX, clientY);
        setOverHighlight(slotFromPoint(clientX, clientY));
      });
    };

    const onUp = (e) => {
      const d = dragRef.current;
      if (!d) return;
      if (d.pointerId != null && e.pointerId !== d.pointerId) return;
      const toSlot = slotFromPoint(e.clientX, e.clientY);
      if (toSlot != null) swapSlots(d.slotIndex, toSlot);
      clearDrag();
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [swapSlots, clearDrag, placeGhost, slotFromPoint, setOverHighlight]);

  useEffect(() => () => clearDrag(), [clearDrag]);

  return (
    <div
      className={`freeform-jigsaw${solved ? " freeform-jigsaw--solved" : ""}${
        dragIndex != null ? " freeform-jigsaw--dragging" : ""
      }`}
    >
      <svg
        ref={svgRef}
        className="freeform-jigsaw__svg"
        viewBox={`0 0 ${boardW} ${boardH}`}
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        role="img"
        aria-label="Interlocking jigsaw puzzle"
      >
        <defs>
          {placement.map((pieceId, slotIndex) => {
            const geo = geometry[pieceId];
            const slotCol = slotIndex % cols;
            const slotRow = Math.floor(slotIndex / cols);
            const dx = slotCol * cw - geo.col * cw;
            const dy = slotRow * ch - geo.row * ch;
            return (
              <clipPath
                key={`slot-clip-${slotIndex}`}
                id={`ff-${uid}-slot-${slotIndex}`}
              >
                <path d={translatePath(geo.path, dx, dy)} />
              </clipPath>
            );
          })}
        </defs>

        {Array.from({ length: rows * cols }, (_, slotIndex) => {
          const col = slotIndex % cols;
          const row = Math.floor(slotIndex / cols);
          return (
            <rect
              key={`guide-${slotIndex}`}
              ref={(el) => {
                guideElsRef.current[slotIndex] = el;
              }}
              x={col * cw}
              y={row * ch}
              width={cw}
              height={ch}
              className="freeform-jigsaw__guide"
            />
          );
        })}

        {placement.map((pieceId, slotIndex) => {
          const geo = geometry[pieceId];
          const slotCol = slotIndex % cols;
          const slotRow = Math.floor(slotIndex / cols);
          const dx = slotCol * cw - geo.col * cw;
          const dy = slotRow * ch - geo.row * ch;
          const isLocked = locked[slotIndex];
          const isLifted = dragIndex === slotIndex;

          return (
            <g
              key={`slot-${slotIndex}`}
              className={`freeform-jigsaw__piece${
                isLocked ? " freeform-jigsaw__piece--locked" : ""
              }${isLifted ? " freeform-jigsaw__piece--lifted" : ""}`}
              onPointerDown={(e) => onPointerDown(e, slotIndex)}
              style={{ cursor: isLocked || solved ? "default" : "grab" }}
            >
              <image
                href={imageUrl}
                x={dx}
                y={dy}
                width={boardW}
                height={boardH}
                preserveAspectRatio="none"
                clipPath={`url(#ff-${uid}-slot-${slotIndex})`}
                pointerEvents="none"
              />
              <path
                d={translatePath(geo.path, dx, dy)}
                className="freeform-jigsaw__outline"
                fill="transparent"
                pointerEvents="all"
              />
            </g>
          );
        })}

        {showFull ? (
          <image
            href={imageUrl}
            x={0}
            y={0}
            width={boardW}
            height={boardH}
            preserveAspectRatio="none"
            className="freeform-jigsaw__full"
            pointerEvents="none"
          />
        ) : null}
      </svg>

      {ghost &&
        createPortal(
          <svg
            ref={bindGhostEl}
            className="freeform-jigsaw__ghost"
            width={ghost.width}
            height={ghost.height}
            viewBox={`0 0 ${ghost.viewW} ${ghost.viewH}`}
            aria-hidden
          >
            <defs>
              <clipPath id={`ff-${uid}-ghost`}>
                <path d={ghost.path} />
              </clipPath>
            </defs>
            <image
              href={imageUrl}
              x={ghost.imgX}
              y={ghost.imgY}
              width={boardW}
              height={boardH}
              preserveAspectRatio="none"
              clipPath={`url(#ff-${uid}-ghost)`}
            />
            <path d={ghost.path} className="freeform-jigsaw__ghost-outline" />
          </svg>,
          document.body
        )}
    </div>
  );
}
